import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Col, Form, type FormInstance, Row } from 'antd';
import type moment from 'moment';

import type { RootState } from '@/redux/RootReducer';
import PostalCodeInput, { type PostalCodeInputValues } from '@/views/components/Selectors/PostalCodeInput';
import { CountryCallingCodeSelect, CountrySelect, StateSelect } from '@/views/screens/common';

import { WEEKS_DAYS } from '../../../../contants/common';
import { saveShipmentAddressBookInProgress } from '../../../../redux/shipment/ShipmentActions';
import { valueToString } from '../../../../utilz';
import { formateWithMoment } from '../../../../utilz/dates';
import { routeFileSelector } from '../../../../utilz/i18n';
import Button from '../../../components/Buttons/Button';
import FormInput from '../../../components/Inputs/FormInput';
import FormSelectBox from '../../../components/Inputs/FormSelectBox';
import FormTextArea from '../../../components/Inputs/FormTextArea';
import FormTimePicker from '../../../components/Inputs/FormTimePicker';
import HiddenFormInput from '../../../components/Inputs/HiddenFormInput';
import FormLabel from '../../../components/Labels/FormLabel';
import AppointmentSelector from '../../../components/Selectors/AppointmentSelector';
import type { VendorAddressAppointment } from '../../VendorAddress/types';
import type { CountryInfo } from '../types';

export interface AddLocationFormValues {
  name: string;
  addr1?: string;
  addr2: string;
  searchId?: string;
  countryCode: string; // US
  zip?: string;
  state?: string;
  city?: string;
  appointmentType?: VendorAddressAppointment;
  fromOperationDay?: string;
  toOperationDay?: string;
  fromOperationHours?: string | moment.Moment;
  toOperationHours?: string | moment.Moment;
  countryCallingCode?: string;
  phone?: string;
  locationId: number;
  phoneExt?: string;
  contactEmail?: string;
  contactName?: string;
}

interface AddLocationFormProps {
  form: FormInstance<AddLocationFormValues>;
  handleSuccess: (data: AddLocationFormValues) => void;
  handleOnClickSearch?: () => void;
  initValues?: Partial<AddLocationFormValues>;
}

const AddLocationForm: FC<AddLocationFormProps> = ({ form, handleSuccess, handleOnClickSearch, initValues }) => {
  const dispatch = useDispatch();
  const { selectedCompany, user } = useSelector((state: RootState) => state.auth);
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);

  const handleZipDetails = (data: PostalCodeInputValues) => {
    form.setFieldValue('countryCode', data?.countryCode);
    form.setFieldValue('city', data?.city);
    form.setFieldValue('state', data?.state);
    form.setFieldValue('zip', data?.zip);
  };

  const handleSubmit = (data: AddLocationFormValues) => {
    const payload = {
      ...data,
      callback: handleCallback,
      createdBy: user?.data?.id,
      customerKey: selectedCompany?.companyId,
      daysOfWeek: `${valueToString(data.fromOperationDay)}-${valueToString(data.toOperationDay)}`,
      hoursOfOperation: `${formateWithMoment(
        data.fromOperationHours,
        'hh:mm A',
      )}-${formateWithMoment(data.toOperationHours, 'hh:mm A')}`,
      phone: `${valueToString(data.countryCallingCode)}-${valueToString(data.phone)}`,
    };
    dispatch(saveShipmentAddressBookInProgress(payload));
  };

  const handleCallback = (data: AddLocationFormValues) => {
    form.resetFields();
    if (handleSuccess) {
      handleSuccess(data);
    }
  };

  const handleCountryChange = (_data?: CountryInfo) => {
    // TODO: this code wasn't working before. Investigate countryCallingCode for handleCountryChange
    // if (data?.countryCallingCode) form.setFieldValue('countryCallingCode', `+${data?.countryCallingCode}`);
    form.setFieldValue('zip', undefined);
    form.setFieldValue('state', undefined);
    form.setFieldValue('city', undefined);
  };

  const countryCode = Form.useWatch('countryCode', form);

  return (
    <Form<AddLocationFormValues>
      form={form}
      onFinish={handleSubmit}
      initialValues={{ addr2: ' ', locationId: 0, ...initValues }}
      preserve={false}
    >
      <Row gutter={30}>
        <Col className="w-20">
          <FormInput
            name="name"
            label={t('translation:default.fields.companyName.label')}
            editMode
            rules={[
              {
                message: t('translation:default.fields.companyName.message'),
                required: true,
              },
            ]}
            margin="mb-20"
          />
          <FormTextArea
            name="addr1"
            label={t('translation:default.fields.companyAddress.label')}
            editMode
            rows={4}
            margin="mb-15"
          />
          <FormInput
            name="siteId"
            label={t('translation:addressBook.siteId.label')}
            editMode
            rules={[
              {
                max: 35,
                message: t('translation:default.fields.maxLengthMessage', {
                  field: t('translation:addressBook.siteId.label'),
                  length: 35,
                }),
              },
            ]}
            margin="mb-0"
          />
        </Col>
        <Col className="w-20">
          <FormInput name="searchId" label={t('translation:default.fields.location.label')} editMode margin="mb-15" />
          <CountrySelect
            name="countryCode"
            label={t('translation:default.fields.country.label')}
            editMode
            rules={[
              {
                message: t('translation:default.fields.country.message'),
                required: true,
              },
            ]}
            onChange={handleCountryChange}
            margin="mb-15"
          />
          <PostalCodeInput
            name="zip"
            editMode
            countryCode={countryCode}
            handleZipDetails={handleZipDetails}
            rules={[
              {
                message: t('translation:default.fields.postalCode.message'),
                required: true,
              },
            ]}
            margin="mb-0"
          />
        </Col>
        <Col className="w-20">
          <StateSelect
            name="state"
            countryCode={countryCode}
            rules={[
              {
                message: t('translation:default.fields.state.message'),
                required: true,
              },
            ]}
            margin="mb-12"
          />
          <FormInput
            name="city"
            label={t('translation:default.fields.city.label')}
            editMode
            rules={[
              {
                message: t('translation:default.fields.city.message'),
                required: true,
              },
            ]}
            margin="mb-15"
          />
          {/* @ts-ignore TODO: within shipment types tasks */}
          <AppointmentSelector
            label={t('translation:default.fields.appointmentType.label')}
            name="appointmentType"
            margin="mb-0"
          />
        </Col>
        <Col className="w-20">
          <FormInput
            name="contactName"
            label={t('translation:default.fields.contactName.label')}
            editMode
            margin="mb-15"
          />
          <div className="mb-15">
            <FormLabel>{t('translation:default.fields.contactNumber')}</FormLabel>
            <Row gutter={10} className="mt-2">
              <Col sm={6}>
                <CountryCallingCodeSelect
                  margin="mb-0"
                  editMode
                  name="countryCallingCode"
                  placeholder={t('translation:default.fields.code')}
                  label=""
                />
              </Col>
              <Col sm={13}>
                <FormInput
                  margin="mb-0"
                  editMode
                  name="phone"
                  placeholder={t('translation:default.fields.number')}
                  inputType="number"
                />
              </Col>
              <Col sm={5}>
                <FormInput
                  placeholder={t('translation:parcelExpress.locationForm.ext')}
                  margin="mb-0"
                  editMode
                  name="phoneExt"
                />
              </Col>
            </Row>
          </div>
          <FormInput
            name="contactEmail"
            label={t('translation:default.fields.contactEmailAddress.label')}
            editMode
            margin="mb-0"
            rules={[
              {
                message: t('translation:default.notification.email'),
                type: 'email',
              },
            ]}
          />
        </Col>
        <Col className="w-20">
          <Row gutter={10} className="w-100 mb-2 mb-15">
            <Col sm={24} className="mb-1">
              <FormLabel className="form-input-label">{t('translation:default.fields.daysOfWeek')}</FormLabel>
            </Col>
            <Col sm={12}>
              <FormSelectBox
                options={WEEKS_DAYS}
                editMode
                label={t('translation:default.fields.from.label')}
                colon
                name="fromOperationDay"
                margin="mb-0"
                placeholder={t('translation:default.fields.from.placeholder')}
              />
            </Col>
            <Col sm={12}>
              <FormSelectBox
                options={WEEKS_DAYS}
                editMode
                label={t('translation:default.fields.to.label')}
                colon
                name="toOperationDay"
                placeholder={t('translation:default.fields.to.placeholder')}
                margin="mb-0"
              />
            </Col>
          </Row>

          <Row gutter={10} className="w-100 mb-15">
            <Col sm={24} className="mb-2">
              <FormLabel className="form-input-label">{t('translation:default.fields.hoursOfOperations')}</FormLabel>
            </Col>
            <Col sm={12}>
              <FormTimePicker
                label={t('translation:default.fields.from.label')}
                colon
                editMode
                name="fromOperationHours"
                margin="mb-15"
              />
            </Col>
            <Col sm={12}>
              <FormTimePicker
                label={t('translation:default.fields.to.label')}
                colon
                editMode
                name="toOperationHours"
                margin="mb-15"
              />
            </Col>
          </Row>
          <div className="pr-10">
            <Row className="flex-nowrap" justify="space-between">
              <Col>
                <Button buttonType="submit">{t('translation:default.buttons.add')}</Button>
              </Col>
              {handleOnClickSearch && (
                <Col>
                  <Button onClick={handleOnClickSearch}>{t('translation:default.buttons.search')}</Button>
                </Col>
              )}
              <Col>
                <Button type="default" onClick={() => form.resetFields()}>
                  {t('translation:default.fields.reset')}
                </Button>
              </Col>
            </Row>
          </div>
        </Col>
      </Row>
      <HiddenFormInput name="locationId" />
      <HiddenFormInput name="addr2" />
    </Form>
  );
};

export default AddLocationForm;
